// The game store: one zustand store over the last 30 days, the window the
// whole app works on. At load it reads the window's records (one read) and
// every one of their facts; older games stay in storage and are never read,
// nor are their scans. Kept current by the games API's events and completed
// scans, and by the window moving on: every change drops the games that have
// aged out, recomputes the registered aggregators in one pass and publishes a
// new state.
import { createStore, type StoreApi } from 'zustand';
import {
  changeBandOf,
  conversionsOf,
  inPeriod,
  middlegameTurnsOf,
  periodStart,
  runAggregators,
  splitStageChange,
  valuesOf,
  type Aggregator,
  type AggregatorId,
  type ChangeBand,
  type GameBlunder,
  type GameFacts,
  type GameFilter,
  type GameQuery,
  type GameRecord,
  type GameStore,
  type GameSummary,
  type Phase,
  type Scan,
  type StoredGame,
  type StoreState,
} from '@chess/api-model';
import { matchesFilter } from '@chess/api-games/filter';
import { PeriodAggregator } from './period.ts';

/** How many games' facts are read at once. */
const READ_AT_ONCE = 16;

/** What the store needs of the platform, so it runs on fakes in tests. */
export interface StoreSources {
  /** The whole records of the games played after `since` (ISO 8601). */
  records(since: string): Promise<GameRecord[]>;
  /** One game's whole record. */
  record(id: string): Promise<GameRecord | undefined>;
  /** The game's facts from its record (moves, stages and stored scan read fresh); undefined when the reader played neither side or it is unfinished. */
  facts(record: GameRecord): Promise<GameFacts | undefined>;
  /** The game's stored scan. */
  scan(id: string): Promise<Scan | undefined>;
  /** Whether a game's facts show this kind of blunder: the `blunder` filter's rule. */
  hasBlunder(facts: GameFacts, kind: GameBlunder): boolean;
  /** The `mistake` filter's keys of a game (`gameMistakeKeys`, with the motif keys from the `mistakes.motifs` service), for the period ending `now`; synchronous, so the service's answers are memoised by the caller. */
  mistakeKeys(facts: GameFacts, now: number): ReadonlySet<string>;
  now(): number;
  warn(message: string, fields?: Record<string, unknown>): void;
  /**
   * The account whose games the store holds (the chess.com username in
   * settings), compared without case; undefined or empty holds every
   * account's. A game of another account stays in storage, unread, like one
   * older than the window.
   */
  owner?(): string | undefined;
}

/** A registered aggregator, and how many registrations hold it (two plugins may share one). */
interface Registration {
  aggregator: Aggregator<unknown>;
  holders: number;
}

/** The PGN's header block: every line up to the first that is not a tag pair. */
export function headerBlock(pgn: string): string {
  const lines: string[] = [];
  for (const line of pgn.split(/\r?\n/)) {
    if (!line.trim()) {
      if (lines.length) break;
      continue;
    }
    if (!line.startsWith('[')) break;
    lines.push(line);
  }
  return lines.join('\n');
}

function summaryOf(record: GameRecord): GameSummary {
  const { pgn: _pgn, schemaVersion: _version, ...summary } = record;
  return summary;
}

/** Newest first; the id breaks a tie so the order never depends on how the games were read. */
const newestFirst = (a: StoredGame, b: StoredGame) => b.summary.playedAt.localeCompare(a.summary.playedAt) || a.summary.id.localeCompare(b.summary.id);

/** The band of a game's score change across a stage, from its facts; undefined when the stage has no measure. */
export function stageChangeBand(f: GameFacts, stage: Phase): ChangeBand | undefined {
  const pawns = stage === 'opening' ? f.openingScore : f.stageSwings?.[stage];
  return pawns === undefined ? undefined : changeBandOf(pawns);
}

export class GameStoreService implements GameStore {
  private readonly sources: StoreSources;
  private readonly zustand: StoreApi<StoreState>;
  private readonly index = new Map<string, StoredGame>();
  private readonly aggregators = new Map<AggregatorId, Registration>();
  private readonly period = new PeriodAggregator();
  /** Bumped per game on every change, so a slow read never overwrites a newer one. */
  private readonly versions = new Map<string, number>();
  private readonly loaded: Promise<void>;
  private settleLoad!: () => void;

  constructor(sources: StoreSources) {
    this.sources = sources;
    this.zustand = createStore<StoreState>(() => ({ status: 'loading', games: [], results: {}, revision: 0 }));
    this.loaded = new Promise((resolve) => (this.settleLoad = resolve));
  }

  getState(): StoreState {
    return this.zustand.getState();
  }

  subscribe(listener: (state: StoreState) => void): () => void {
    return this.zustand.subscribe((state) => listener(state));
  }

  ready(): Promise<void> {
    return this.loaded;
  }

  game(id: string): StoredGame | undefined {
    return this.index.get(id);
  }

  list(query: GameQuery = {}): GameSummary[] {
    const matched = this.matching(query.owner, query.filter);
    if (query.order === 'oldest') matched.reverse();
    const offset = Math.max(0, query.offset ?? 0);
    const end = query.size === undefined ? undefined : offset + Math.max(0, query.size);
    return matched.slice(offset, end).map((g) => g.summary);
  }

  count(query: Pick<GameQuery, 'owner' | 'filter'> = {}): number {
    return this.matching(query.owner, query.filter).length;
  }

  resolveFilter(filter: GameFilter): GameFilter {
    // The criteria that live in the scan's facts (`blunder`, `endingType`, `endingResult`, `middlegameResult`, `groundCause`, `middlegamePosition`, `conversion`, `stageChange`, `middlegameTurn`, `mistake`): all set ones must hold.
    const kinds = valuesOf(filter.blunder);
    const types = valuesOf(filter.endingType);
    const results = valuesOf(filter.endingResult);
    const middles = valuesOf(filter.middlegameResult);
    const causes = valuesOf(filter.groundCause);
    const positions = valuesOf(filter.middlegamePosition);
    const conversions = valuesOf(filter.conversion);
    const changes = valuesOf(filter.stageChange).map(splitStageChange);
    const turns = valuesOf(filter.middlegameTurn);
    const mistakes = valuesOf(filter.mistake);
    if (!kinds.length && !types.length && !results.length && !middles.length && !causes.length && !positions.length && !conversions.length && !changes.length && !turns.length && !mistakes.length) return filter;
    const now = this.sources.now();
    const ids = this.getState()
      .games.filter((g) => {
        const f = g.facts;
        if (!f) return false;
        if (kinds.length && !kinds.some((kind) => this.sources.hasBlunder(f, kind))) return false;
        if (types.length && (f.endingType === undefined || !types.includes(f.endingType))) return false;
        if (results.length && (f.endingResult === undefined || !results.includes(f.endingResult))) return false;
        if (middles.length && (f.middlegameResult === undefined || !middles.includes(f.middlegameResult))) return false;
        if (causes.length && (f.groundCause === undefined || !causes.includes(f.groundCause))) return false;
        if (turns.length && !middlegameTurnsOf(f).some((t) => turns.includes(t))) return false;
        if (positions.length && !positions.some((p) => f.middlegamePositions?.[p] !== undefined)) return false;
        if (conversions.length && !conversionsOf(f).some((c) => conversions.includes(c))) return false;
        if (mistakes.length) {
          const keys = this.sources.mistakeKeys(f, now);
          if (!mistakes.some((k) => keys.has(k))) return false;
        }
        return !changes.length || changes.some(({ stage, band }) => stageChangeBand(f, stage) === band);
      })
      .map((g) => g.summary.id);
    return { ...filter, ids };
  }

  record(id: string): Promise<GameRecord | undefined> {
    return this.sources.record(id);
  }

  scan(id: string): Promise<Scan | undefined> {
    return this.sources.scan(id);
  }

  registerAggregator<R>(aggregator: Aggregator<R>): () => void {
    const held = this.aggregators.get(aggregator.id);
    // The same id again: a reloaded plugin's new instance replaces the old one.
    this.aggregators.set(aggregator.id, { aggregator: aggregator as Aggregator<unknown>, holders: (held?.holders ?? 0) + 1 });
    if (this.getState().status === 'ready') this.publish({}, [aggregator as Aggregator<unknown>]);
    let done = false;
    return () => {
      if (done) return;
      done = true;
      const r = this.aggregators.get(aggregator.id);
      if (!r) return;
      if (r.holders > 1) {
        r.holders -= 1;
        return;
      }
      this.aggregators.delete(aggregator.id);
      const { [aggregator.id]: _gone, ...results } = this.getState().results;
      this.set({ results });
    };
  }

  result<R>(aggregator: Aggregator<R>): R | undefined {
    return this.getState().results[aggregator.id] as R | undefined;
  }

  /** Reads the window: its records once, and each one's facts. */
  async load(): Promise<void> {
    try {
      const since = new Date(periodStart('30d', this.sources.now())).toISOString();
      const records = (await this.sources.records(since)).filter((r) => this.held(r));
      for (let i = 0; i < records.length; i += READ_AT_ONCE) {
        const batch = records.slice(i, i + READ_AT_ONCE);
        const facts = await Promise.all(batch.map((r) => this.readFacts(r)));
        batch.forEach((r, j) => this.index.set(r.id, this.stored(r, facts[j])));
      }
      this.publish({ status: 'ready' });
    } catch (error) {
      this.sources.warn('the game store failed to load', { error: String(error) });
      this.set({ status: 'failed' });
    } finally {
      this.settleLoad();
    }
  }

  /**
   * Drops every held game and reads the window again, as when the account
   * changes: the games held are the new account's. The state is `loading`
   * meanwhile, so every reader shows its skeleton rather than the old
   * account's numbers.
   */
  async reload(): Promise<void> {
    await this.loaded;
    this.index.clear();
    for (const id of this.versions.keys()) this.bump(id);
    this.set({ status: 'loading', games: [], period: undefined });
    await this.load();
  }

  /**
   * Reads every held game's facts again, as when the blunder and miss rules
   * change: the facts follow the rules, the records do not change. One
   * publish at the end; a game changed meanwhile by an event keeps the newer
   * read. Waits for the first load.
   */
  async refreshFacts(): Promise<void> {
    await this.loaded;
    const ids = [...this.index.keys()];
    for (let i = 0; i < ids.length; i += READ_AT_ONCE) {
      const batch = ids.slice(i, i + READ_AT_ONCE);
      await Promise.all(
        batch.map(async (id) => {
          const version = this.bump(id);
          const record = await this.sources.record(id).catch(() => undefined);
          if (!record) return;
          const facts = await this.readFacts(record);
          if (this.versions.get(id) === version && this.index.has(id)) this.index.set(id, this.stored(record, facts));
        }),
      );
    }
    if (this.getState().status === 'ready') this.publish({});
  }

  /** A game was added, or replaced: read it again; one played before the window is not held. */
  async added(id: string): Promise<void> {
    await this.reread(id, true);
  }

  removed(id: string): void {
    this.bump(id);
    if (this.index.delete(id) && this.getState().status === 'ready') this.publish({});
  }

  /** A game's scan completed: its engine facts change. Games outside the window are not read. */
  async scanned(id: string): Promise<void> {
    if (this.index.has(id)) await this.reread(id, false);
  }

  /** Reads a game's record and facts again and holds the result, unless a newer change overtook the read. */
  private async reread(id: string, isNew: boolean): Promise<void> {
    const version = this.bump(id);
    const record = await this.sources.record(id).catch((error) => {
      this.sources.warn('the game store could not read a game', { id, error: String(error) });
      return undefined;
    });
    const held = !!record && this.held(record);
    const facts = held ? await this.readFacts(record) : undefined;
    if (this.versions.get(id) !== version) return;
    if (held) this.index.set(id, this.stored(record, facts));
    else if (isNew || !record) this.index.delete(id);
    if (this.getState().status === 'ready') this.publish({});
  }

  /** Held games matching the owner and filter, newest first. */
  private matching(owner: string | undefined, filter: GameFilter | undefined): StoredGame[] {
    const games = this.getState().games;
    if (!owner && !filter) return [...games];
    const now = this.sources.now();
    return games.filter(
      (g) => (!owner || g.summary.owner === owner) && (!filter || matchesFilter({ ...g.summary, pgn: g.headers }, filter, now)),
    );
  }

  private inWindow(playedAt: string): boolean {
    return inPeriod(Date.parse(playedAt), '30d', this.sources.now());
  }

  /** Whether the store holds this record: in the window, and the account's when one is set. */
  private held(record: Pick<GameRecord, 'playedAt' | 'owner'>): boolean {
    if (!this.inWindow(record.playedAt)) return false;
    const owner = this.sources.owner?.();
    return !owner || record.owner.toLowerCase() === owner.toLowerCase();
  }

  private stored(record: GameRecord, facts: GameFacts | undefined): StoredGame {
    return { summary: summaryOf(record), headers: headerBlock(record.pgn), facts };
  }

  private bump(id: string): number {
    const v = (this.versions.get(id) ?? 0) + 1;
    this.versions.set(id, v);
    return v;
  }

  private async readFacts(record: GameRecord): Promise<GameFacts | undefined> {
    try {
      return await this.sources.facts(record);
    } catch (error) {
      this.sources.warn('the game store could not read a game\'s facts', { id: record.id, error: String(error) });
      return undefined;
    }
  }

  /**
   * Publishes the held games, after dropping those the window has left,
   * recomputing the period summary and the given aggregators (all by default)
   * in one pass. An aggregator that throws loses its result and is logged;
   * the rest stand.
   */
  private publish(patch: Partial<StoreState>, only?: Aggregator<unknown>[]): void {
    for (const [id, g] of this.index) if (!this.inWindow(g.summary.playedAt)) this.index.delete(id);
    const games = [...this.index.values()].sort(newestFirst);
    const facts = games.map((g) => g.facts).filter((f): f is GameFacts => f !== undefined);
    const aggregators = only ?? [...this.aggregators.values()].map((r) => r.aggregator);
    const now = this.sources.now();
    const ran = runAggregators([this.period as Aggregator<unknown>, ...aggregators], facts, now, ({ id, error }) =>
      this.sources.warn('an aggregator failed', { id, error: String(error) }),
    );
    const results: Record<AggregatorId, unknown> = { ...(only ? this.getState().results : {}) };
    for (const a of aggregators) {
      if (ran.has(a.id)) results[a.id] = ran.get(a.id);
      else delete results[a.id];
    }
    const period = ran.get(this.period.id) as StoreState['period'];
    this.set({ ...patch, games, results, ...(period ? { period } : {}) });
  }

  private set(patch: Partial<StoreState>): void {
    const current = this.getState();
    this.zustand.setState({ ...current, ...patch, revision: current.revision + 1 }, true);
  }
}
